import { collectionChangedJoinPointFilter, ContextKeys, isDocumentWithIncludedInventoryItems, propertyChangedJoinPointFilter, withModule } from '@skylab-shared/business';
import { BusinessObjectPart, BusinessObjectPartContext, CollectionChangedJoinPoint, PropertyChangedJoinPoint, Weaver as WeaverBase } from '@skylab-shared/core';
import { InventoryEffect, InventoryGoodsReceiptDocumentModel, PurchaseDocumentKind, PurchaseInvoiceDocumentItemModel, PurchaseInvoiceDocumentModel } from '@skylab-shared/dataModel';

export class PurchaseInvoiceIssuedToInventoryGoodsReceiptBoPart extends BusinessObjectPart<PurchaseInvoiceDocumentModel> {
    public constructor(
        weaver: WeaverBase
    ) {
        super(weaver);
    }

    public override async initializeAsync(context: BusinessObjectPartContext<PurchaseInvoiceDocumentModel>): Promise<void> {
        await super.initializeAsync(context);
        this.registerReadonlyProperties();
        this.synchronizeInventoryEffect();
    }

    private registerReadonlyProperties(): void {
        const itemModel = PurchaseInvoiceDocumentItemModel;

        this.engine.readonly.addRule(
            [[itemModel, 'productId'], [itemModel, 'productName'], [itemModel, 'unitId']],
            [[itemModel, 'productName'], [itemModel, 'unitId'],
        ], ctx => ctx.source.includesInventoryControl);

        this.engine.readonly.addRule([
            [this.modelType, 'inventoryEffect'],
        ], () => !isDocumentWithIncludedInventoryItems(this.bo.model)  || this.documentIssuedToInventoryGoodsReceipt || this.isRealizedExistingDocument);

        this.engine.readonly.addRule([
            [this.modelType, 'seller'],
        ], () => (this.documentIssuedToInventoryGoodsReceipt && this.bo.model.isPersistent) || !!this.relatedGoodsReceiptDocument?.seller);

        this.engine.readonly.addRule([
            [this.modelType, 'deliveryDate']
        ], () => this.documentIssuedToInventoryGoodsReceipt);

        this.engine.readonly.addRule(
            [[itemModel, 'productId'], [itemModel, 'productName'], [itemModel, 'quantity'], [itemModel, 'unitId']],
            [[itemModel, 'productName'], [itemModel, 'quantity'], [itemModel, 'unitId'],
        ], ctx =>  ctx.source.includesInventoryControl && (this.documentIssuedToInventoryGoodsReceipt || this.isRealizedExistingDocument));
    }

    private get isRealizedExistingDocument(): boolean {
        return this.bo.model.isPersistent && !this.bo.model.modifiedProperties.has('inventoryEffect') && (this.bo.model.inventoryEffect === InventoryEffect.Realized);
    }

    private get documentIssuedToInventoryGoodsReceipt(): boolean {
        return this.bo.localContext.getPreloaded(ContextKeys.isInvoiceToInventoryGoodsReceipt(this.bo.model.sun));
    }

    private get relatedGoodsReceiptDocument(): InventoryGoodsReceiptDocumentModel | null {
        if (PurchaseDocumentKind.PurchaseInvoice !== this.bo.model.kind) return null;
        return this.bo.localContext.getPreloaded(ContextKeys.relatedGoodsReceiptDocumentToInvoice(this.bo.model.sun));
    }

    private synchronizeInventoryEffect(): void {
        const handle = () => {
            const withInventory = isDocumentWithIncludedInventoryItems(this.bo.model);
            if (this.bo.model.inventoryEffect && withInventory) return;
            if (this.bo.model.inventoryEffect && !withInventory) this.bo.model.inventoryEffect = null;
            if (!this.bo.model.inventoryEffect && withInventory)  this.bo.model.inventoryEffect = withModule('Inventory') ? InventoryEffect.Realized : InventoryEffect.NotRealized;
        };

        this.registerListening(this.weaver.register(CollectionChangedJoinPoint, collectionChangedJoinPointFilter(this.bo)(this.bo.model.items)(
            _ => handle()
        )));

        this.registerListening(this.weaver.register(PropertyChangedJoinPoint, propertyChangedJoinPointFilter(this.bo)(PurchaseInvoiceDocumentItemModel, ['includesInventoryControl'])(
            _ => handle()
        )));
    }
}
